/**
 * Such-Treffer-Highlighting — pure, DOM-frei, client+server-importierbar.
 *
 * `splitHighlight(text, query)` zerlegt `text` in zusammenhängende Segmente
 * `{ text, match }`, wobei alle literalen Vorkommen der Query-Tokens
 * `match: true` tragen. Rein kosmetisch: zeigt, WO die Query literal im
 * Dateinamen (bzw. Stack-Label) vorkommt. Der Match ist FTS+Trigram gemischt
 * (`search-match.ts`); reine Fuzzy-/Tippfehler-Treffer haben kein literales
 * Vorkommen → kein Segment markiert, das ist gewollt (kein falsches Highlight).
 *
 * Design (siehe tasks/spec.md):
 *  - **NFC beidseitig** (#179/#182): macOS-Dateinamen sind oft NFD, die Query
 *    ist NFC. Gematcht UND gerendert wird die NFC-Form (visuell identisch zum
 *    NFD-Original) → kein NFD↔NFC-Index-Mapping nötig.
 *  - **Token-Extraktion**: an Whitespace + `-_.` splitten, je Stück auf
 *    `\p{L}\p{N}` reduzieren (Sonderzeichen wie `%&/` raus), lowercasen, nur
 *    Tokens ≥2 Zeichen behalten (gegen Highlight-Rauschen einzelner Buchstaben).
 *  - **Substring-Suche per `toLowerCase().indexOf`** — NIE `new RegExp(userInput)`
 *    (Injection/ReDoS). Tokens sind keine Pattern, nur Literale.
 *  - **Merge**: überlappende/benachbarte Matches verschmelzen zu einem Segment
 *    (kein verschachteltes `<mark>`) — fällt automatisch aus der Boolean-Maske.
 */

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Extrahiert die distinkten Highlight-Tokens aus dem rohen Query-String:
 * NFC → lowercase → an Whitespace/`-_.` splitten → je Stück auf Buchstaben/
 * Ziffern reduzieren → nur Tokens ≥2 Zeichen. Reihenfolge irrelevant (die
 * Maske ist token-kommutativ), Duplikate via Set entfernt.
 */
export function extractHighlightTokens(query: string): string[] {
  const normalized = query.normalize('NFC').toLowerCase();
  const seen = new Set<string>();
  for (const raw of normalized.split(/[\s\-_.]+/)) {
    const token = raw.replace(/[^\p{L}\p{N}]/gu, '');
    if (token.length >= 2) seen.add(token);
  }
  return [...seen];
}

/**
 * Zerlegt `text` in matched/unmatched Segmente gegen die Query-Tokens.
 * Leerer Text oder token-loser Query → ein einziges unmatched Segment
 * (Caller rendert dann Plain-Text ohne `<mark>`).
 */
export function splitHighlight(text: string, query: string): HighlightSegment[] {
  const nfcText = text.normalize('NFC');
  const tokens = extractHighlightTokens(query);
  if (nfcText.length === 0 || tokens.length === 0) {
    return [{ text: nfcText, match: false }];
  }

  const lower = nfcText.toLowerCase();
  // Defensiv: `toLowerCase()` ist für praktisch alle Datei-/Produktnamen
  // (Latein, deutsche Umlaute) längenerhaltend, aber NICHT garantiert — z.B.
  // 'İ' (U+0130) → 'i̇' (2 Zeichen). Dann driftet die Index-Zuordnung zwischen
  // `lower` und `nfcText`. Highlighting ist kosmetisch → bei Längen-Mismatch
  // lieber kein Highlight als ein falsch positioniertes (sauberer Plain-Text).
  if (lower.length !== nfcText.length) {
    return [{ text: nfcText, match: false }];
  }

  // Boolean-Maske: welche Zeichen-Indizes liegen in einem Token-Vorkommen?
  const matched = new Array<boolean>(nfcText.length).fill(false);
  for (const token of tokens) {
    let from = 0;
    for (;;) {
      const idx = lower.indexOf(token, from);
      if (idx === -1) break;
      for (let i = idx; i < idx + token.length; i++) matched[i] = true;
      from = idx + 1; // +1 (nicht +len): findet auch überlappende Vorkommen
    }
  }

  // Maske → zusammenhängende Segmente. Benachbarte/überlappende Matches teilen
  // denselben `matched`-Wert → verschmelzen automatisch zu einem Segment.
  const segments: HighlightSegment[] = [];
  let start = 0;
  for (let i = 1; i <= nfcText.length; i++) {
    if (i === nfcText.length || matched[i] !== matched[start]) {
      segments.push({ text: nfcText.slice(start, i), match: matched[start] });
      start = i;
    }
  }
  return segments;
}
